import { useState, useEffect } from "react";
import { Country, State, City } from "country-state-city";

const Address = ({ newEmployee, setNewEmployee }) => {
  const [sameAsPermanent, setSameAsPermanent] = useState(false);

  // For dropdown options
  const countries = Country.getAllCountries();

  // Find country code for permanent and communication
  const permCountryCode = countries.find(c => c.name === newEmployee.permanent_country)?.isoCode || "";
  const commCountryCode = countries.find(c => c.name === newEmployee.communication_country)?.isoCode || "";

  // States based on country code
  const permStates = permCountryCode ? State.getStatesOfCountry(permCountryCode) : [];
  const commStates = commCountryCode ? State.getStatesOfCountry(commCountryCode) : [];

  // Cities based on country and state code
  const permStateCode = permStates.find(s => s.name === newEmployee.permanent_state)?.isoCode || "";
  const commStateCode = commStates.find(s => s.name === newEmployee.communication_state)?.isoCode || "";

  const permCities = permCountryCode && permStateCode ? City.getCitiesOfState(permCountryCode, permStateCode) : [];
  const commCities = commCountryCode && commStateCode ? City.getCitiesOfState(commCountryCode, commStateCode) : [];

  useEffect(() => {
    if (sameAsPermanent) {
      setNewEmployee((prev) => ({
        ...prev,
        communication_building_no: prev.permanent_building_no,
        communication_locality: prev.permanent_locality,
        communication_landmark: prev.permanent_landmark,
        communication_city: prev.permanent_city,
        communication_state: prev.permanent_state,
        communication_pincode: prev.permanent_pincode,
        communication_country: prev.permanent_country,
      }));
    }
  }, [sameAsPermanent, setNewEmployee]);

  // Helper to update country/state/city but clear dependent fields on change
  const handlePermanentCountryChange = (e) => {
    const countryName = e.target.value;
    setNewEmployee((prev) => ({
      ...prev,
      permanent_country: countryName,
      permanent_state: "",
      permanent_city: "",
    }));
  };

  const handlePermanentStateChange = (e) => {
    const stateName = e.target.value;
    setNewEmployee((prev) => ({
      ...prev,
      permanent_state: stateName,
      permanent_city: "",
    }));
  };

  const handleCommunicationCountryChange = (e) => {
    const countryName = e.target.value;
    setNewEmployee((prev) => ({
      ...prev,
      communication_country: countryName,
      communication_state: "",
      communication_city: "",
    }));
  };

  const handleCommunicationStateChange = (e) => {
    const stateName = e.target.value;
    setNewEmployee((prev) => ({
      ...prev,
      communication_state: stateName,
      communication_city: "",
    }));
  };

  return (
    <>
      {/* Permanent Address */}
      <h5 className="mt-3">Permanent Address <span style={{ color: "red" }}>*</span></h5>
      <div className="d-flex gap-2 flex-wrap mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="Building No"
          value={newEmployee.permanent_building_no}
          onChange={(e) => setNewEmployee({ ...newEmployee, permanent_building_no: e.target.value })}
          required
          style={{ minWidth: "120px" }}
        />
        <input
          type="text"
          className="form-control"
          placeholder="Locality"
          value={newEmployee.permanent_locality}
          onChange={(e) => setNewEmployee({ ...newEmployee, permanent_locality: e.target.value })}
          required
          style={{ minWidth: "130px" }}
        />
        <input
          type="text"
          className="form-control"
          placeholder="Landmark (optional)"
          value={newEmployee.permanent_landmark}
          onChange={(e) => setNewEmployee({ ...newEmployee, permanent_landmark: e.target.value })}
          style={{ minWidth: "130px" }}
        />

        {/* Country dropdown */}
        <select
          className="form-control"
          value={newEmployee.permanent_country}
          onChange={handlePermanentCountryChange}
          required
          style={{ minWidth: "130px" }}
        >
          <option value="">Select Country</option>
          {countries.map((country) => (
            <option key={country.isoCode} value={country.name}>
              {country.name}
            </option>
          ))}
        </select>

        {/* State dropdown */}
        <select
          className="form-control"
          value={newEmployee.permanent_state}
          onChange={handlePermanentStateChange}
          required
          style={{ minWidth: "130px" }}
          disabled={!permCountryCode}
        >
          <option value="">Select State</option>
          {permStates.map((state) => (
            <option key={state.isoCode} value={state.name}>
              {state.name}
            </option>
          ))}
        </select>

        {/* City dropdown */}
        <select
          className="form-control"
          value={newEmployee.permanent_city}
          onChange={(e) => setNewEmployee({ ...newEmployee, permanent_city: e.target.value })}
          required
          style={{ minWidth: "130px" }}
          disabled={!permStateCode}
        >
          <option value="">Select City</option>
          {permCities.map((city) => (
            <option key={city.name} value={city.name}>
              {city.name}
            </option>
          ))}
        </select>

        <input
          type="text"
          className="form-control"
          placeholder="Pincode"
          value={newEmployee.permanent_pincode}
          onChange={(e) => setNewEmployee({ ...newEmployee, permanent_pincode: e.target.value })}
          required
          style={{ minWidth: "100px" }}
        />
      </div>

      {/* Checkbox: Same as Permanent */}
      <div className="form-check mb-3">
        <input
          className="form-check-input"
          type="checkbox"
          id="sameAddressCheck"
          checked={sameAsPermanent}
          onChange={() => setSameAsPermanent((prev) => !prev)}
        />
        <label className="form-check-label" htmlFor="sameAddressCheck">
          Communication address same as permanent
        </label>
      </div>

      {/* Communication Address */}
      <h5>Communication Address</h5>
      <div className="d-flex gap-2 flex-wrap mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="Building No"
          value={newEmployee.communication_building_no}
          onChange={(e) => setNewEmployee({ ...newEmployee, communication_building_no: e.target.value })}
          disabled={sameAsPermanent}
          style={{ minWidth: "120px" }}
        />
        <input
          type="text"
          className="form-control"
          placeholder="Locality"
          value={newEmployee.communication_locality}
          onChange={(e) => setNewEmployee({ ...newEmployee, communication_locality: e.target.value })}
          disabled={sameAsPermanent}
          style={{ minWidth: "130px" }}
        />
        <input
          type="text"
          className="form-control"
          placeholder="Landmark (optional)"
          value={newEmployee.communication_landmark}
          onChange={(e) => setNewEmployee({ ...newEmployee, communication_landmark: e.target.value })}
          disabled={sameAsPermanent}
          style={{ minWidth: "130px" }}
        />

        {/* Communication Country dropdown */}
        <select
          className="form-control"
          value={newEmployee.communication_country}
          onChange={handleCommunicationCountryChange}
          disabled={sameAsPermanent}
          style={{ minWidth: "130px" }}
        >
          <option value="">Select Country</option>
          {countries.map((country) => (
            <option key={country.isoCode} value={country.name}>
              {country.name}
            </option>
          ))}
        </select>

        {/* Communication State dropdown */}
        <select
          className="form-control"
          value={newEmployee.communication_state}
          onChange={handleCommunicationStateChange}
          disabled={sameAsPermanent || !commCountryCode}
          style={{ minWidth: "130px" }}
        >
          <option value="">Select State</option>
          {commStates.map((state) => (
            <option key={state.isoCode} value={state.name}>
              {state.name}
            </option>
          ))}
        </select>

        {/* Communication City dropdown */}
        <select
          className="form-control"
          value={newEmployee.communication_city}
          onChange={(e) => setNewEmployee({ ...newEmployee, communication_city: e.target.value })}
          disabled={sameAsPermanent || !commStateCode}
          style={{ minWidth: "130px" }}
        >
          <option value="">Select City</option>
          {commCities.map((city) => (
            <option key={city.name} value={city.name}>
              {city.name}
            </option>
          ))}
        </select>

        <input
          type="text"
          className="form-control"
          placeholder="Pincode"
          value={newEmployee.communication_pincode}
          onChange={(e) => setNewEmployee({ ...newEmployee, communication_pincode: e.target.value })}
          disabled={sameAsPermanent}
          style={{ minWidth: "100px" }}
        />
      </div>
    </>
  );
};

export default Address;
